import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Section } from "src/pages/Settings/SettingsControls";
import { useAuth } from "src/context/AuthProvider";
import { RecencyChip } from "src/components/RecencyChip/RecencyChip";
import { ApiError } from "src/services/apiClient";
import {
    listPendingAgentUndos, undoAgentCommit, listAmbiguousMatches, dismissAmbiguousMatch,
    type PendingAgentUndo, type AmbiguousMatch,
} from "src/services/agentSyncService";
import { ConfirmPopup } from "src/components/Popup/ConfirmPopup";
import "./agentAttentionSection.scss";

const POLL_MS = 30_000;

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "");

function minutesLeft(expiresAt: string, now: number): number {
    return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / 60000));
}

/** What the desktop agent did on its own that staff may need to act on: recent imports that can
 *  still be undone, and imports it refused because two existing patients matched by name + birth
 *  date. Renders nothing while both lists are empty, so a practice that isn't using the agent
 *  never sees it. */
function AgentAttentionPanel() {
    const { t } = useTranslation();
    const [undos, setUndos] = useState<PendingAgentUndo[]>([]);
    const [ambiguous, setAmbiguous] = useState<AmbiguousMatch[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [now, setNow] = useState(() => Date.now());

    const load = useCallback(async () => {
        try {
            const [u, a] = await Promise.all([listPendingAgentUndos(), listAmbiguousMatches()]);
            setUndos(u);
            setAmbiguous(a);
        } catch {
            // Best-effort panel: a failed poll keeps what is on screen instead of raising an error
            // banner every 30 s. Actions below report their own failures.
        }
    }, []);

    useEffect(() => {
        void load();
        const timer = window.setInterval(() => {
            if (document.hidden) return;
            setNow(Date.now());
            void load();
        }, POLL_MS);
        return () => window.clearInterval(timer);
    }, [load]);

    const [pendingUndo, setPendingUndo] = useState<PendingAgentUndo | null>(null);
    const handleUndo = async (item: PendingAgentUndo) => {
        setPendingUndo(null);
        setBusyId(item.id);
        setError(null);
        try {
            await undoAgentCommit(item.id);
        } catch (e) {
            const status = e instanceof ApiError ? e.status : 0;
            setError(
                status === 409 ? t("agentAttention.undoAlreadyDone")
                    : status === 410 ? t("agentAttention.undoExpired")
                        : status === 404 ? t("agentAttention.undoNotFound")
                            : t("agentAttention.undoError"),
            );
        } finally {
            setBusyId(null);
            await load();
        }
    };

    const handleDismiss = async (item: AmbiguousMatch) => {
        setBusyId(item.eventId);
        setError(null);
        try {
            await dismissAmbiguousMatch(item.eventId);
        } catch {
            setError(t("agentAttention.dismissError"));
        } finally {
            setBusyId(null);
            await load();
        }
    };

    if (undos.length === 0 && ambiguous.length === 0) return null;

    return (
        <Section title={t("agentAttention.title")} description={t("agentAttention.description")}>
            <div className="agent-attention">
                {error && <p className="agent-attention__error" role="alert">{error}</p>}

                {pendingUndo && (
                    <ConfirmPopup
                        message={t("agentAttention.undoConfirm", { name: `${pendingUndo.firstName} ${pendingUndo.lastName}`.trim() })}
                        onConfirm={() => void handleUndo(pendingUndo)}
                        onCancel={() => setPendingUndo(null)}
                    />
                )}

                {ambiguous.length > 0 && (
                    <div className="agent-attention__group">
                        <h3 className="agent-attention__heading">{t("agentAttention.ambiguousTitle")}</h3>
                        <p className="agent-attention__hint">{t("agentAttention.ambiguousHint")}</p>
                        <ul className="agent-attention__list">
                            {ambiguous.map((m) => {
                                const name = `${m.firstName} ${m.lastName}`.trim();
                                return (
                                    <li key={m.eventId} className="agent-attention__item agent-attention__item--warn">
                                        <div className="agent-attention__main">
                                            <span className="agent-attention__name">
                                                {name || t("agentAttention.unknownPatient")}
                                            </span>
                                            <span className="agent-attention__meta">
                                                {m.birthDate && <span>{t("agentAttention.born", { date: formatDate(m.birthDate) })}</span>}
                                                <span>{t("agentAttention.externalId", { id: m.externalId })}</span>
                                                <RecencyChip timestamp={m.lastSeenAt} />
                                            </span>
                                            {m.candidates.length > 0 && (
                                                <span className="agent-attention__candidates">
                                                    {t("agentAttention.candidates")}
                                                    {m.candidates.map((c, i) => (
                                                        <Link key={c.patientId} to={`/patients/${c.patientId}`} className="agent-attention__link">
                                                            {t("agentAttention.candidateLink", { n: i + 1 })}
                                                        </Link>
                                                    ))}
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn-outline"
                                            onClick={() => void handleDismiss(m)}
                                            disabled={busyId === m.eventId}
                                        >
                                            {t("agentAttention.dismissBtn")}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {undos.length > 0 && (
                    <div className="agent-attention__group">
                        <h3 className="agent-attention__heading">{t("agentAttention.undoTitle")}</h3>
                        <ul className="agent-attention__list">
                            {undos.map((u) => (
                                <li key={u.id} className="agent-attention__item">
                                    <div className="agent-attention__main">
                                        <span className="agent-attention__name">
                                            {`${u.firstName} ${u.lastName}`.trim() || u.externalId}
                                        </span>
                                        <span className="agent-attention__meta">
                                            <span>{u.created ? t("agentAttention.created") : t("agentAttention.updated")}</span>
                                            <RecencyChip timestamp={u.committedAt} />
                                            <span>{t("agentAttention.minutesLeft", { count: minutesLeft(u.undoWindowExpiresAt, now) })}</span>
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-outline"
                                        onClick={() => setPendingUndo(u)}
                                        disabled={busyId === u.id}
                                    >
                                        {t("agentAttention.undoBtn")}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </Section>
    );
}

/** Gated like the backend's requireRecipients (role default Doctor/Admin, else an explicit
 *  permRecipients) — the same rule PatientPage uses for its write actions. */
export function AgentAttentionSection() {
    const { user } = useAuth();
    const role = user?.doctor?.userRights;
    const canWrite = user?.doctor?.permRecipients !== undefined
        ? user.doctor.permRecipients
        : (role === 2 || role === 3);
    return canWrite ? <AgentAttentionPanel /> : null;
}
